import { Link } from "react-router-dom";
import { ArrowRight, Compass, ListChecks, Question, Target } from "@phosphor-icons/react";
import ProductPageShell from "@/components/ProductPageShell";
import { DEMO_RELATIONSHIP_DATA } from "@/data/liveDemoData";
import { trackPublicEvent } from "@/lib/publicAnalytics";

const source = "product_prepare_next_call";
const activePath = "/product/prepare-next-call";

function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function ProductPrepareNextCallPage() {
  const prep = DEMO_RELATIONSHIP_DATA.relationship_memory?.prepare_next_call || {};
  const objections = Array.isArray(prep.likely_objections) ? prep.likely_objections.slice(0, 2) : [];
  const callFlow = Array.isArray(prep.suggested_call_flow) ? prep.suggested_call_flow.slice(0, 4) : [];
  const questions = Array.isArray(prep.questions_to_ask) ? prep.questions_to_ask.slice(0, 2) : [];

  return (
    <ProductPageShell source={source} activePath={activePath}>
      <section className="product-feature-hero px-5 pb-16 pt-16 sm:px-8 sm:pb-24 sm:pt-28">
        <div className="product-feature-hero__glow" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.94fr_1.06fr] lg:items-center lg:gap-16">
          <div>
            <div className="landing-anti-summary-pill mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-[0.2em] marketing-text-secondary">
              <span className="landing-anti-summary-icon" aria-hidden="true">
                <ArrowRight size={14} weight="bold" />
              </span>
              <span>Prepare Next Call</span>
            </div>
            <h1 className="product-feature-hero__title landing-hero-title max-w-3xl text-[2.7rem] font-semibold leading-[1.01] tracking-[-0.055em] sm:text-6xl lg:text-7xl">
              <span className="product-feature-hero__title-base block">Walk into the next call</span>
              <span className="product-feature-hero__title-accent landing-hero-spectrum landing-hero-promise block">knowing what matters.</span>
            </h1>
            <p className="product-feature-hero-subcopy landing-hero-subcopy mt-7 max-w-2xl text-lg font-medium leading-8 marketing-text-secondary sm:text-xl sm:leading-9">
              The previous conversation should not leave you with a recap to reread. Callsect turns the relationship state into a practical plan: what to validate, what to ask, what to expect, and what the next conversation needs to accomplish.
            </p>
            <div className="product-feature-hero__actions mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/login?mode=register" onClick={() => track("hero", "sign_up_free")} className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition">
                Sign up free
              </Link>
              <Link to="/demo" onClick={() => track("hero", "relationship_demo")} className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition">
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>

          <article className="product-next-call-preview">
            <div className="product-next-call-preview__head">
              <span><Compass size={18} weight="duotone" /> Prepare Next Call</span>
              <strong>Walk in with a plan, not just a recap.</strong>
            </div>
            <div className="product-next-call-preview__strategy">
              <span>Plan</span>
              <strong>{prep.conversation_strategy}</strong>
            </div>
            <div className="product-next-call-preview__pair">
              <div>
                <span>Your goal</span>
                <strong>{prep.objective?.text}</strong>
              </div>
              <div>
                <span>Likely objection</span>
                <strong>{objections[0]?.text}</strong>
              </div>
            </div>
            <div className="product-next-call-preview__question">
              <span>Question to ask</span>
              <blockquote>“{questions[0]?.text}”</blockquote>
            </div>
          </article>
        </div>
      </section>

      <section className="product-feature-section marketing-band border-y px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="product-feature-section__intro">
            <span>From history to plan</span>
            <h2>Four things should carry into the next conversation.</h2>
            <p>The point is not to create a longer pre-call brief. It is to preserve the few pieces of context that materially change how you should enter the call.</p>
          </div>

          <div className="product-feature-grid product-feature-grid--four mt-12">
            <article className="product-feature-card">
              <Target size={24} weight="duotone" aria-hidden="true" />
              <span>Objective</span>
              <h3>Know what this call needs to accomplish.</h3>
              <p>Translate the relationship state into one concrete outcome instead of starting with a generic agenda.</p>
            </article>
            <article className="product-feature-card">
              <Compass size={24} weight="duotone" aria-hidden="true" />
              <span>Relationship state</span>
              <h3>Start from what is true now.</h3>
              <p>Carry forward the active objection, stakeholder dynamics, commitments, buying intent, and risk.</p>
            </article>
            <article className="product-feature-card">
              <Question size={24} weight="duotone" aria-hidden="true" />
              <span>Questions</span>
              <h3>Ask what moves the relationship forward.</h3>
              <p>Bring the unresolved questions and decision dependencies back into the conversation deliberately.</p>
            </article>
            <article className="product-feature-card">
              <ListChecks size={24} weight="duotone" aria-hidden="true" />
              <span>Call flow</span>
              <h3>Know what to validate, surface, and avoid.</h3>
              <p>Use the previous conversation to shape the order and purpose of the next one.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="product-feature-section px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.76fr_1.24fr] lg:items-start lg:gap-16">
          <div className="product-feature-sticky product-feature-sticky--no-kicker">
            <h2>Preparation should answer the questions your notes cannot.</h2>
            <p>What is still unresolved? Which stakeholder matters next? What will the customer probably push back on? What should you handle differently this time?</p>
          </div>

          <div className="product-next-call-detail">
            <section>
              <span>Likely objections</span>
              {objections.map((item) => (
                <div key={item.text}>
                  <strong>{item.text}</strong>
                  {item.response_direction ? <p>{item.response_direction}</p> : null}
                </div>
              ))}
            </section>
            <section>
              <span>Suggested call flow</span>
              <ol>
                {callFlow.map((item, index) => (
                  <li key={item.title}>
                    <b>{index + 1}</b>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.purpose}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
            <section>
              <span>Questions to ask</span>
              {questions.map((item) => <blockquote key={item.text}>“{item.text}”</blockquote>)}
            </section>
          </div>
        </div>
      </section>

      <section className="product-feature-band px-5 py-14 sm:px-8 sm:py-16">
        <div className="marketing-highlight-band mx-auto flex max-w-6xl flex-col gap-6 rounded-3xl border p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Compass size={25} weight="duotone" aria-hidden="true" />
            <h2>The next call starts with the relationship you already have.</h2>
            <p>Preparation gets stronger when Callsect remembers the customer context and the coaching lesson together.</p>
          </div>
          <Link to="/product/relationship-memory" className="product-feature-inline-link">
            See relationship memory
            <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>
    </ProductPageShell>
  );
}
